import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, ArrowRight } from "lucide-react";

const empty = { name: "", type: "SUSHI STATION", address: "", phone: "718-216-2013", is_active: true, hours: "Mon – Sun: 9:00 AM – 6:00 PM ET" };

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/stores").then((r) => setStores(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) await api.put(`/stores/${editing.id}`, form);
      else await api.post("/stores", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleOrdering = async (s, v) => {
    try { await api.patch(`/stores/${s.id}/ordering`, { online_ordering: v }); toast.success(`Online ordering ${v ? "enabled" : "disabled"} for ${s.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, type: s.type, address: s.address, phone: s.phone, is_active: s.is_active, hours: s.hours || "Mon – Sun: 9:00 AM – 6:00 PM ET" }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Delete store? This clears its pricing overrides.")) return;
    try { await api.delete(`/stores/${id}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">Locations</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-stores-title">Stores</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="add-store"><Plus className="w-4 h-4 mr-1"/>Add Store</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Store" : "New Store"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="store-name"/></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUSHI STATION">Sushi Station</SelectItem>
                    <SelectItem value="GRAB-AND-GO">Grab-and-Go</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/></div>
              <div><Label>Hours</Label><Input value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} placeholder="Mon – Sun: 9:00 AM – 6:00 PM ET" data-testid="store-hours"/></div>
              <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})}/><Label>Active</Label></div>
              <Button onClick={save} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="store-save">{editing ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((s) => (
          <div key={s.id} className="group p-5 bg-white rounded-2xl border border-neutral-200/70 hover:border-[hsl(var(--primary))]/40 hover:shadow-sm transition-all" data-testid={`admin-store-${s.id}`}>
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[hsl(var(--primary))]">{s.type}</span>
              <span className={`w-2 h-2 rounded-full ${s.is_active ? "bg-emerald-500" : "bg-neutral-300"}`}/>
            </div>
            <Link to={`/admin/stores/${s.id}`} className="block" data-testid={`open-store-${s.id}`}>
              <h3 className="font-display font-bold text-xl mt-2 group-hover:text-[hsl(var(--primary))]">{s.name}</h3>
              <p className="text-sm text-neutral-500 mt-2 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5"/>{s.address}</p>
              <p className="text-xs text-neutral-500 mt-1">{s.hours || "Mon – Sun: 9:00 AM – 6:00 PM ET"}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]">Open store dashboard <ArrowRight className="w-3 h-3"/></div>
            </Link>
            <label className="mt-4 flex items-center justify-between gap-2 rounded-xl bg-neutral-50 px-3 py-2 text-xs" data-testid={`ordering-toggle-${s.id}`}>
              <span className="font-medium">Online ordering {s.online_ordering ? <span className="text-emerald-600">· ON</span> : <span className="text-neutral-400">· off</span>}</span>
              <Switch checked={!!s.online_ordering} onCheckedChange={(v) => toggleOrdering(s, v)}/>
            </label>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="flex-1">Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(s.id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3"/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
